import { dayKey, dayLabel } from "./expense-list";
import { formatCents } from "./format";

export const SOMEONE = "Jemand";
const UNKNOWN = "Unbekannt";

const timeFormat = new Intl.DateTimeFormat("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
});

const dateTimeFormat = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const quoted = (text) => `„${text ?? ""}“`;

const nameOrUnknown = (name) => name || UNKNOWN;

const formatDateTime = (iso) => {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? "?" : dateTimeFormat.format(time);
};

export const formatActivityTime = (iso) => {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? "" : timeFormat.format(time);
};

const isPaymentSummary = (summary) => summary?.type === "payment";

const isIncomeSummary = (summary) => summary?.type === "income";

const SPLIT_LABELS = { weights: "Anteile", percent: "Prozent" };

const receiversOf = (summary) => summary?.receivers ?? [];

const paidFor = (receivers) =>
  receivers.filter((receiver) => receiver.share > 0).map((r) => r.name);

const recipientOf = (summary) => nameOrUnknown(paidFor(receiversOf(summary))[0] ?? receiversOf(summary)[0]?.name);

const paymentLabel = (summary) =>
  `die Zahlung ${nameOrUnknown(summary.payer)} → ${recipientOf(summary)}`;

const entryLabel = (summary) => {
  if (isPaymentSummary(summary)) return paymentLabel(summary);
  if (isIncomeSummary(summary)) return `die Einnahme ${quoted(summary.title)}`;
  return quoted(summary?.title);
};

const entryLabelWithAmount = (summary) =>
  `${entryLabel(summary)} (${formatCents(summary?.amount ?? 0)})`;

const signedList = (added, removed) =>
  [...added.map((name) => `+${nameOrUnknown(name)}`), ...removed.map((name) => `−${nameOrUnknown(name)}`)].join(" ");

const missingNames = (names, others) => names.filter((name) => !others.includes(name));

const isProportional = (before, after, [amountBefore, amountAfter]) =>
  before.every((receiver, index) => {
    const scaled = receiver.share * amountAfter;
    return Math.abs(scaled - after[index].share * amountBefore) <= Math.max(amountBefore, amountAfter);
  });

const sameSplitValues = (before, after) =>
  before.length === after.length &&
  before.every((r, i) => (r.split_value ?? null) === (after[i].split_value ?? null));

const receiverChange = (summary, { before = [], after = [] }, amount, splitModeChanged) => {
  if (isPaymentSummary(summary)) {
    const from = recipientOf({ receivers: before });
    const to = recipientOf({ receivers: after });
    return from === to ? null : `An ${from} → ${to}`;
  }
  const namesBefore = paidFor(before);
  const namesAfter = paidFor(after);
  const added = missingNames(namesAfter, namesBefore);
  const removed = missingNames(namesBefore, namesAfter);
  if (added.length || removed.length) return `Für ${signedList(added, removed)}`;
  if (splitModeChanged) return null;
  const sameOrder = before.length === after.length && before.every((r, i) => r.name === after[i].name);
  const proportional = amount && sameOrder && isProportional(before, after, amount);
  if (proportional && sameSplitValues(before, after)) return null;
  return "Aufteilung geändert";
};

export const describeChanges = (summary) => {
  const changes = summary?.changes ?? {};
  const parts = [];
  if (changes.title) {
    parts.push(`Titel ${quoted(changes.title[0])} → ${quoted(changes.title[1])}`);
  }
  if (changes.amount) {
    parts.push(`Betrag ${formatCents(changes.amount[0])} → ${formatCents(changes.amount[1])}`);
  }
  if (changes.payer) {
    const label = isPaymentSummary(summary)
      ? "Von"
      : isIncomeSummary(summary)
        ? "Erhalten von"
        : "Bezahlt von";
    parts.push(`${label} ${nameOrUnknown(changes.payer[0])} → ${nameOrUnknown(changes.payer[1])}`);
  }
  if (changes.receivers) {
    const part = receiverChange(summary, changes.receivers, changes.amount, Boolean(changes.split_mode));
    if (part) parts.push(part);
  }
  if (changes.split_mode) {
    parts.push(`Aufteilung: ${SPLIT_LABELS[changes.split_mode[1]] ?? "Beträge"}`);
  }
  if (changes.spent_at) {
    parts.push(`Zeitpunkt ${formatDateTime(changes.spent_at[0])} → ${formatDateTime(changes.spent_at[1])}`);
  }
  if (changes.tags) {
    parts.push(`Tags ${signedList(changes.tags.added ?? [], changes.tags.removed ?? [])}`);
  }
  if (changes.description) {
    const [before, after] = changes.description;
    parts.push(!before ? "Notiz hinzugefügt" : !after ? "Notiz entfernt" : "Notiz geändert");
  }
  return parts;
};

const THIRD_PERSON = { has: "hat", is: "ist", self: "sich", own: "die" };
const SECOND_PERSON = { has: "hast", is: "bist", self: "dich", own: "deine" };
export const YOU = "Du";

const updatedText = (summary, g) => {
  const parts = describeChanges(summary);
  const head = `${g.has} ${entryLabel(summary)} geändert`;
  return parts.length ? `${head}: ${parts.join(", ")}` : head;
};

const entryTexts = {
  created: (summary, g) => `${g.has} ${entryLabelWithAmount(summary)} hinzugefügt`,
  updated: updatedText,
  deleted: (summary, g) => `${g.has} ${entryLabelWithAmount(summary)} gelöscht`,
};

const INTERVAL_WORDS = { weekly: "wöchentlich", monthly: "monatlich", yearly: "jährlich" };

const intervalWord = (interval) => INTERVAL_WORDS[interval] ?? "wiederkehrend";

const formatDateKey = (key) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key ?? "");
  return match ? `${match[3]}.${match[2]}.${match[1]}` : "?";
};

const recurringLabel = (summary) => `die wiederkehrende Ausgabe ${quoted(summary?.title)}`;

const recurringLabelWithDetails = (summary) =>
  `${recurringLabel(summary)} (${formatCents(summary?.amount ?? 0)}, ${intervalWord(summary?.interval)})`;

const endChange = ([before, after]) => {
  if (!after) return "ohne Ende";
  if (!before) return `Ende am ${formatDateKey(after)}`;
  return `Ende ${formatDateKey(before)} → ${formatDateKey(after)}`;
};

export const describeRecurringChanges = (summary) => {
  const changes = summary?.changes ?? {};
  const parts = describeChanges(summary);
  if (changes.interval) {
    parts.push(`Rhythmus ${intervalWord(changes.interval[0])} → ${intervalWord(changes.interval[1])}`);
  }
  if (changes.start_date) {
    parts.push(
      `Beginn ${formatDateKey(changes.start_date[0])} → ${formatDateKey(changes.start_date[1])}`,
    );
  }
  if (changes.end_date) parts.push(endChange(changes.end_date));
  return parts;
};

const recurringUpdatedText = (summary, g) => {
  const parts = describeRecurringChanges(summary);
  const head = `${g.has} ${recurringLabel(summary)} geändert`;
  return parts.length ? `${head}: ${parts.join(", ")}` : head;
};

const assignedText = (summary, g, mine) => {
  const name = nameOrUnknown(summary.name);
  if (summary.previous_name && mine) {
    return `${g.has} ${g.self} als ${name} statt ${nameOrUnknown(summary.previous_name)} eingetragen`;
  }
  if (mine || summary.previous_name) return `${g.has} ${g.self} als ${name} eingetragen`;
  return `${g.has} ${g.self} eingetragen`;
};

const unassignedText = (summary, g, mine) =>
  mine
    ? `${g.has} ${g.own} Zuordnung zu ${nameOrUnknown(summary.name)} aufgehoben`
    : `${g.has} ${g.own} Zuordnung aufgehoben`;

const selfAddedText = (summary, g, mine) => {
  const previous = summary.previous_name ? ` (vorher ${nameOrUnknown(summary.previous_name)})` : "";
  return mine
    ? `${g.has} ${g.self} als ${nameOrUnknown(summary.name)} hinzugefügt${previous}`
    : `${g.has} ${g.self} hinzugefügt${previous}`;
};

const texts = {
  "participant.added": (summary, g) => `${g.has} ${nameOrUnknown(summary.name)} hinzugefügt`,
  "participant.renamed": (summary, g) =>
    `${g.has} ${nameOrUnknown(summary.previous_name)} in ${quoted(summary.name)} umbenannt`,
  "participant.deleted": (summary, g) => `${g.has} ${nameOrUnknown(summary.name)} entfernt`,
  "group.created": (summary, g) => `${g.has} die Gruppe ${quoted(summary.name)} erstellt`,
  "group.renamed": (summary, g) =>
    `${g.has} die Gruppe ${quoted(summary.previous_name)} in ${quoted(summary.name)} umbenannt`,
  "member.joined": (summary, g) => `${g.is} beigetreten`,
  "member.left": (summary, g) => `${g.has} die Gruppe verlassen`,
  "member.assigned": assignedText,
  "member.unassigned": unassignedText,
  "tag.renamed": (summary, g) =>
    `${g.has} den Tag ${quoted(summary.previous_name)} in ${quoted(summary.name)} umbenannt`,
  "tag.deleted": (summary, g) => `${g.has} den Tag ${quoted(summary.name)} gelöscht`,
  "recurring.created": (summary, g) => `${g.has} ${recurringLabelWithDetails(summary)} angelegt`,
  "recurring.updated": recurringUpdatedText,
  "recurring.paused": (summary, g) => `${g.has} ${recurringLabel(summary)} pausiert`,
  "recurring.resumed": (summary, g) => `${g.has} ${recurringLabel(summary)} fortgesetzt`,
  "recurring.deleted": (summary, g) => `${g.has} ${recurringLabelWithDetails(summary)} gelöscht`,
};

const textFor = (action, summary, g, mine) => {
  const [entity, verb] = String(action ?? "").split(".");
  const entryText = (entity === "expense" || entity === "payment") && entryTexts[verb];
  if (entryText) {
    return entryText({ ...summary, type: entity === "payment" ? "payment" : summary.type }, g);
  }
  return texts[action]?.(summary, g, mine) ?? `${g.has} etwas geändert`;
};

const subjectOf = (entry, summary) => {
  if (entry?.action === "member.unassigned") return entry.actor || nameOrUnknown(summary.name);
  if (entry?.action === "member.assigned" && !summary.previous_name) {
    return nameOrUnknown(summary.name);
  }
  if (entry?.action === "member.assigned") return entry.actor || nameOrUnknown(summary.previous_name);
  return entry?.actor || SOMEONE;
};

export const activityParts = (entry) => {
  const summary = entry?.summary ?? {};
  const mine = entry?.mine === true;
  const g = mine ? SECOND_PERSON : THIRD_PERSON;
  if (entry?.action === "participant.added" && summary.self) {
    return {
      subject: mine ? YOU : nameOrUnknown(summary.name),
      predicate: selfAddedText(summary, g, mine),
    };
  }
  return {
    subject: mine ? YOU : subjectOf(entry, summary),
    predicate: textFor(entry?.action, summary, g, mine),
  };
};

export const describeActivity = (entry) => {
  const { subject, predicate } = activityParts(entry);
  return `${subject} ${predicate}`;
};

export const groupActivityByDay = (entries = [], now = new Date()) => {
  const days = [];
  for (const entry of entries) {
    const key = dayKey(Date.parse(entry.created_at) || 0);
    let day = days[days.length - 1];
    if (!day || day.key !== key) {
      day = { key, label: dayLabel(key, now), entries: [] };
      days.push(day);
    }
    day.entries.push(entry);
  }
  return days;
};
